/**
 * Swiper loader + deferred-init coordinator.
 *
 * Exposes:
 *  - window.Swiper          → the Swiper class (available immediately)
 *  - window.whenSwiperReady → defers a carousel's init until the page is idle
 *                             or a `.swiper` element nears the viewport, so the
 *                             ~95ms Swiper init reflow happens AFTER first paint
 *                             instead of blocking page load.
 *
 * Why a static import (not `import('swiper/bundle')`):
 *   The theme's script build (scripts/build-scripts.sh) compiles each file with
 *   a plain `esbuild --bundle` (no code-splitting / esm output). A dynamic
 *   import can't be bundled that way and makes the build fail, which leaves the
 *   carousel scripts stale and out of sync — the cause of "Swiper is not
 *   defined". A static import bundles cleanly and guarantees window.Swiper is
 *   always set, so carousels can never hit an undefined Swiper.
 */
import Swiper from '../../../node_modules/swiper/swiper-bundle.mjs';

window.Swiper = Swiper;

// Coordinate with the inline head stub (window.whenSwiperReady + queue), which
// is defined early so it always exists even if a carousel script loads first.
// Fallback definition here in case that stub is ever absent.
if (typeof window.whenSwiperReady !== 'function') {
    window._swiperReady = false;
    window._swiperQueue = [];
    window.whenSwiperReady = (callback) => {
        if (typeof callback !== 'function') return;
        if (window._swiperReady) callback();
        else window._swiperQueue.push(callback);
    };
}

const flushReady = () => {
    if (window._swiperReady) return;
    window._swiperReady = true;
    document.dispatchEvent(new CustomEvent('swiper:loaded'));
    const queue = window._swiperQueue || [];
    while (queue.length) {
        const cb = queue.shift();
        try {
            cb();
        } catch (err) {
            console.error('[NSS][Swiper] init error', err);
        }
    }
};

// Initialize carousels when one nears the viewport (keeps the init reflow off
// the critical load path), with an idle fallback so they never hang.
const observer = new IntersectionObserver(
    (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
            observer.disconnect();
            flushReady();
        }
    },
    { rootMargin: '300px 0px' }
);

const scheduleInit = () => {
    document.querySelectorAll('.swiper').forEach((el) => observer.observe(el));

    // Safety net: always init after the browser is idle, even if no `.swiper`
    // is observed (e.g. carousels added later, or none currently in view).
    const idle =
        window.requestIdleCallback || ((fn) => window.setTimeout(fn, 1500));
    idle(() => flushReady());
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleInit);
} else {
    scheduleInit();
}
